import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { engagementsApi, scopePoliciesApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState } from '../components/ui/states';

export function ScopePolicies() {
  const engagementsQuery = useQuery({ queryKey: ['engagements'], queryFn: () => engagementsApi.list() });
  const engagements = engagementsQuery.data?.engagements ?? [];

  const policyQueries = useQueries({
    queries: engagements.map((e) => ({
      queryKey: ['scope-policies', e.id],
      queryFn: () => scopePoliciesApi.list(e.id),
      enabled: engagements.length > 0,
    })),
  });

  if (engagementsQuery.isLoading) return <LoadingState />;
  if (engagementsQuery.isError) return <ErrorState error={engagementsQuery.error} onRetry={() => engagementsQuery.refetch()} />;

  const rows = engagements.flatMap((e, i) => (policyQueries[i]?.data?.scopePolicies ?? []).map((p) => ({ engagement: e, policy: p })));

  return (
    <div>
      <PageHeader title="Scope Policies" description="What's in bounds for testing, by engagement and zone. Deny always wins over allow." />
      {rows.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck size={32} />}
          title="No scope policies yet"
          description="Open an engagement and add a scope policy for the zones you want to test."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map(({ engagement, policy }) => (
            <Link key={policy.id} to={`/engagements/${engagement.id}`}>
              <Card className="transition-shadow hover:shadow-md">
                <CardContent className="pt-6">
                  <p className="font-medium text-ink">{engagement.name}</p>
                  <Badge variant="brand" className="mt-2 capitalize">
                    {policy.zone}
                  </Badge>
                  <p className="mt-2 text-xs text-ink-muted">
                    {policy.policy.allow.domains.length + policy.policy.allow.hosts.length + policy.policy.allow.cidrs.length} allowed
                    entries
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
